import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  LoanDto,
  LoanListResponse,
  CreateLoanRequest,
  UpdateLoanBalanceRequest,
  SetLoanGuarantorsRequest,
  SetLoanAgreementRequest,
} from '@signalvote/shared';
import { apiJson, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';

/** The server's `{ error }` message (an empty one included), else the fallback. */
function serverError(fallback: string) {
  return (body: ApiErrorBody) => new Error(body.error ?? fallback);
}

// FEC-009 (D10) — the candidate personal-funds loan manual-entry surface (Schedule C).
// The loan is a living treasurer-maintained record: recorded once, then its
// outstandingBalance / paidToDate are updated as repayments are made (a repayment is a
// separate SB19A expenditure; v1 does NOT auto-link the repayment bank-line). Backs the
// federal Form 3 Schedule A SA13A receipt + Schedule C SC/10 ledger.

/** GET /api/finance/loans — list the campaign's loans (newest incur date first). */
export function useFinanceLoans() {
  return useQuery({
    queryKey: queryKeys.finance.loans(),
    queryFn: async (): Promise<LoanDto[]> => {
      const body = await apiJson<LoanListResponse>('/api/finance/loans', { error: 'Failed to load loans' });
      return body.items;
    },
  });
}

/** POST /api/finance/loans — record a new candidate loan (balance seeds to the principal). */
export function useCreateLoan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLoanRequest): Promise<LoanDto> =>
      apiJson<LoanDto>('/api/finance/loans', {
        method: 'POST',
        json: data,
        error: serverError('Failed to record loan'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.loans() });
    },
  });
}

/**
 * PUT /api/finance/loans/{id}/guarantors — AFC-004: replace a loan's guarantor set
 * (up to 3, the AL EDI v6.0 cap). Each guarantor emits an `lnGuarantor{N}_*` block on
 * the AL `<receipt reReceiptType=07>` at file time.
 */
export function useSetLoanGuarantors() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: SetLoanGuarantorsRequest;
    }): Promise<LoanDto> =>
      apiJson<LoanDto>(`/api/finance/loans/${id}/guarantors`, {
        method: 'PUT',
        json: data,
        error: serverError('Failed to save guarantors'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.loans() });
    },
  });
}

/**
 * PUT /api/finance/loans/{id}/agreement — GitHub #73: set a federal loan's Schedule C-1
 * loan-agreement details (secured/collateral, lending-representative name + title, and the
 * treasurer / lender signature dates). Emitting these in-app retires the FEC-portal hand-off
 * for the loan agreement. Mirrors `useSetLoanGuarantors`: the request carries the full
 * agreement snapshot and the server returns the updated `LoanDto`.
 */
export function useSetLoanAgreement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: SetLoanAgreementRequest;
    }): Promise<LoanDto> =>
      apiJson<LoanDto>(`/api/finance/loans/${id}/agreement`, {
        method: 'PUT',
        json: data,
        error: serverError('Failed to save loan agreement'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.loans() });
    },
  });
}

/** PATCH /api/finance/loans/{id}/balance — update the running ledger after a repayment. */
export function useUpdateLoanBalance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateLoanBalanceRequest;
    }): Promise<LoanDto> =>
      apiJson<LoanDto>(`/api/finance/loans/${id}/balance`, {
        method: 'PATCH',
        json: data,
        error: serverError('Failed to update loan balance'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.loans() });
    },
  });
}

/**
 * POST /api/finance/loans/{id}/void. Voids a loan entered in error so it drops
 * from a fresh filing's Schedule C / line 10 and from a federal amendment, while the
 * original filing's frozen record stays intact. No concurrency token (mirrors the other
 * loan writes). A loan in a filed, still-locked period returns a 400 the caller surfaces.
 */
export function useVoidLoan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<LoanDto> =>
      apiJson<LoanDto>(`/api/finance/loans/${id}/void`, {
        method: 'POST',
        error: serverError('Failed to void loan'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.loans() });
    },
  });
}

/** POST /api/finance/loans/{id}/unvoid. Restores a voided loan (the inverse). */
export function useUnvoidLoan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<LoanDto> =>
      apiJson<LoanDto>(`/api/finance/loans/${id}/unvoid`, {
        method: 'POST',
        error: serverError('Failed to restore loan'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.loans() });
    },
  });
}
